import { computed, nextTick, ref, watch, watchEffect } from "vue";
import { defineStore } from "pinia";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { useHttpClient } from "@sazka/utils/http-clients/use-http-client";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { useNgCasinoState } from "./use-ng-casino-state";
import { CasinoGameType } from "../model/casino-game-type";

export interface FunparkLastPlayedGame {
    externalSystemId: number;
    pageUrl: any;
    teaserImageUrl: string;
}

export const useNgCasinoFunparkStore = defineStore(
    "ng-casino-funpark-store",
    () => {
        const { isLoggedIn } = useNgSession();
        const { currentGameId, currentGameServerData } = useNgCasinoState();

        const allowedDemoPlaytime = ref<number | null>((window as any).allowedDemoPlaytime || null); // seconds
        const demoPlaytime = ref(0); // seconds
        const demoTimestamp = ref(Date.now());
        const isDemoPlaytimeExceeded = computed(() => {
            if (!allowedDemoPlaytime.value) {
                return false;
            }

            return demoPlaytime.value > allowedDemoPlaytime.value;
        });

        const isFunparkGame = computed(() => {
            return currentGameServerData.value?.type === CasinoGameType.funpark;
        });

        const isLoginRequired = computed(() => {
            return !isLoggedIn.value && isDemoPlaytimeExceeded.value;
        });

        const lastPlayedGameId = ref<string | null>(null);
        const lastPlayedGame = ref<FunparkLastPlayedGame | null>(null);
        let lastPlayedGameFetchInProgress = false;

        const intervalId = ref<number | null>(null);

        const isTheNextDay = () => {
            const currentDate = new Date();
            const lastPlayedDate = new Date(demoTimestamp.value);

            currentDate.setHours(0, 0, 0, 0);
            return currentDate.getTime() > lastPlayedDate.getTime();
        };

        const checkDemoTimestamp = () => {
            if (isTheNextDay()) {
                demoPlaytime.value = 0; // Reset playtime if it's a new day
            }

            demoTimestamp.value = Date.now(); // Update timestamp to current time
        };

        const startTicker = () => {
            if (intervalId.value) return;
            intervalId.value = window.setInterval(() => {
                demoPlaytime.value += 1;
            }, 1000);
        };

        const stopTicker = () => {
            if (intervalId.value) {
                clearInterval(intervalId.value);
                intervalId.value = null;
            }
        };

        const handleDemoEnded = () => {
            if (!isLoginRequired.value || !isFunparkGame.value) return;

            stopTicker();

            eventBus.emit(GlobalEvents.FunparkDemoEnded);
        };

        async function fetchLastPlayedGame() {
            if (!lastPlayedGameId.value || lastPlayedGameFetchInProgress) return;
            if (lastPlayedGameId.value === lastPlayedGame.value?.externalSystemId.toString()) return; // prevent fetching again for the same game

            try {
                lastPlayedGameFetchInProgress = true;

                const funparkClient = useHttpClient();
                funparkClient.defaults.baseURL = "/api/funpark";

                const response = await funparkClient.get<FunparkLastPlayedGame | null>("/game", {
                    params: { id: lastPlayedGameId.value },
                });

                lastPlayedGame.value = response.data;
            } catch (error) {
                logException("Failed to fetch last played game", error);
                lastPlayedGame.value = null;
                return;
            } finally {
                lastPlayedGameFetchInProgress = false;
            }
        }

        function demoPlaytimeTickerWatcher() {
            ng_hs.instantGame.gameLoadStart(() => nextTick(handleDemoEnded));

            watch(
                [currentGameId, isLoggedIn],
                () => {
                    if (currentGameId.value && !isLoggedIn.value && isFunparkGame.value) {
                        startTicker();
                        checkDemoTimestamp();
                    } else {
                        stopTicker();
                    }
                },
                { immediate: true }
            );

            watch(
                isLoginRequired,
                () => {
                    handleDemoEnded();
                },
                { immediate: true }
            );
        }

        function initializeLastPlayedGameWatchers() {
            watch(currentGameServerData, (newGame) => {
                if (newGame && newGame.type === CasinoGameType.funpark) {
                    lastPlayedGameId.value = newGame.gameId.toString();
                    fetchLastPlayedGame();

                    if (!allowedDemoPlaytime.value) {
                        allowedDemoPlaytime.value = newGame.allowedDemoPlaytime;
                    }
                }
            });

            watchEffect(() => {
                if (lastPlayedGameId.value && isLoggedIn.value) {
                    fetchLastPlayedGame();
                }
            });
        }

        async function initializeFunparkCasinoFeatures() {
            await ngFrameworkLoaded();

            checkDemoTimestamp();
            demoPlaytimeTickerWatcher();
            initializeLastPlayedGameWatchers();
        }

        initializeFunparkCasinoFeatures();

        return {
            // Demo playtime
            allowedDemoPlaytime,
            demoPlaytime,
            demoTimestamp,
            isDemoPlaytimeExceeded,
            isFunparkGame,
            isLoginRequired,
            startTicker,
            stopTicker,
            checkDemoTimestamp,

            // Last played game
            lastPlayedGameId,
            lastPlayedGame,
        };
    },
    {
        persist: {
            // eslint-disable-next-line no-restricted-globals
            storage: localStorage,
            pick: ["demoTimestamp", "demoPlaytime", "lastPlayedGameId"],
        },
    }
);
